import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Upload, 
  Save, 
  ArrowLeft,
  Image as ImageIcon,
  X,
  DollarSign,
  Hash,
  FileText
} from 'lucide-react';
import { ProfessionalInput, ProfessionalTextarea, ProfessionalSelect } from '../../components/common/ProfessionalInput';
import api from '../../services/api';
import toast from 'react-hot-toast';

const AddProduct = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shortDescription: '',
    category: '',
    subcategory: '',
    price: '',
    discountPrice: '',
    unit: 'piece',
    stock: '',
    minimumOrderQuantity: 1,
    brand: '',
    model: '',
    warranty: '',
    isActive: true,
    // Specifications
    specifications: {
      grade: '',
      bagWeight: '',
      brand: ''
    }
  });

  const categories = [
    { value: 'cement_concrete', label: 'Cement & Concrete' },
    { value: 'steel_iron', label: 'Steel & Iron' },
    { value: 'bricks_blocks', label: 'Bricks, Blocks & AAC' },
    { value: 'sand_aggregates', label: 'Sand, Aggregates & Soil' },
    { value: 'paints_coatings', label: 'Paints, Coatings & Waterproofing' },
    { value: 'tiles_marble', label: 'Tiles, Marble & Granite' },
    { value: 'plywood_boards', label: 'Plywood, Boards & Laminates' },
    { value: 'electrical_lighting', label: 'Electrical & Lighting' },
    { value: 'plumbing_sanitary', label: 'Plumbing & Sanitary' },
    { value: 'doors_windows', label: 'Doors, Windows & Hardware' },
    { value: 'equipment_tools', label: 'Construction Equipment & Tools' },
    { value: 'roofing_insulation', label: 'Roofing & Insulation' }
  ];

  const units = [
    'piece',
    'kg',
    'ton',
    'meter',
    'square meter',
    'cubic meter',
    'liter',
    'bag',
    'box',
    'bundle'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.category || 
        !formData.price || !formData.stock) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      // Create FormData for file upload
      const productData = new FormData();
      
      // Add form fields
      productData.append('name', formData.name);
      productData.append('description', formData.description);
      productData.append('shortDescription', formData.shortDescription || formData.description.substring(0, 150));
      productData.append('category', formData.category);
      productData.append('subcategory', formData.subcategory || formData.category);
      productData.append('price', formData.price);
      productData.append('discountPrice', formData.discountPrice || '');
      productData.append('unit', formData.unit);
      productData.append('stock', formData.stock);
      productData.append('minimumOrderQuantity', formData.minimumOrderQuantity);
      productData.append('brand', formData.brand || '');
      productData.append('model', formData.model || '');
      productData.append('warranty', formData.warranty || '');
      productData.append('isActive', formData.isActive);
      
      // Add specifications as JSON string
      productData.append('specifications', JSON.stringify(formData.specifications));
      
      // Add images
      if (images.length > 0) {
        images.forEach((image, index) => {
          productData.append('images', image.file);
        });
      }
      
      // Make API call (temporarily allow products without images for testing)
      const response = await api.post('/products', productData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success('Product added successfully!');
        navigate('/seller/products');
      } else {
        toast.error(response.data.message || 'Failed to add product');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add product';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/seller/products')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Add New Product</h1>
          <p className="text-gray-600 mt-2">Create a new product listing for your store</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Package className="w-5 h-5 mr-2 text-primary-600" />
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ProfessionalInput
                label="Product Name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter product name"
                icon={Package}
                required
              />

              <ProfessionalSelect
                label="Category"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                placeholder="Select category"
                options={categories}
                required
              />

              <ProfessionalInput
                label="Brand"
                type="text"
                value={formData.brand}
                onChange={(e) => handleInputChange('brand', e.target.value)}
                placeholder="Enter brand name"
              />

              <ProfessionalInput
                label="Model"
                type="text"
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                placeholder="Enter model number"
              />
            </div>

            <div className="mt-6">
              <ProfessionalTextarea
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe your product in detail..."
                rows={4}
                required
              />
            </div>
          </div>

          {/* Pricing & Inventory */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-primary-600" />
              Pricing & Inventory
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <ProfessionalInput
                label="Price"
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                placeholder="0.00"
                icon={DollarSign}
                required
              />

              <ProfessionalInput
                label="Discount Price"
                type="number"
                value={formData.discountPrice}
                onChange={(e) => handleInputChange('discountPrice', e.target.value)}
                placeholder="0.00"
                icon={DollarSign}
              />

              <ProfessionalSelect
                label="Unit"
                value={formData.unit}
                onChange={(e) => handleInputChange('unit', e.target.value)}
                options={units}
                required
              />

              <ProfessionalInput
                label="Stock Quantity"
                type="number"
                value={formData.stock}
                onChange={(e) => handleInputChange('stock', e.target.value)}
                placeholder="0"
                icon={Hash}
                required
              />

              <ProfessionalInput
                label="Minimum Order Quantity"
                type="number"
                value={formData.minimumOrderQuantity}
                onChange={(e) => handleInputChange('minimumOrderQuantity', e.target.value)}
                placeholder="1"
                icon={Hash}
              />

              <ProfessionalInput
                label="Warranty Period"
                type="text"
                value={formData.warranty}
                onChange={(e) => handleInputChange('warranty', e.target.value)}
                placeholder="e.g., 1 year, 6 months"
              />
            </div>
          </div>

          {/* Specifications (for cement products) */}
          {formData.category === 'cement_concrete' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-primary-600" />
                Product Specifications
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ProfessionalSelect
                  label="Cement Grade"
                  value={formData.specifications.grade}
                  onChange={(e) => handleInputChange('specifications', { ...formData.specifications, grade: e.target.value })}
                  options={[
                    { value: 'OPC 33', label: 'OPC 33' },
                    { value: 'OPC 43', label: 'OPC 43' },
                    { value: 'OPC 53', label: 'OPC 53' },
                    { value: 'PPC', label: 'PPC' },
                    { value: 'PSC', label: 'PSC' }
                  ]}
                  placeholder="Select grade"
                />

                <ProfessionalSelect
                  label="Bag Weight"
                  value={formData.specifications.bagWeight}
                  onChange={(e) => handleInputChange('specifications', { ...formData.specifications, bagWeight: e.target.value })}
                  options={[
                    { value: '25kg', label: '25kg' },
                    { value: '50kg', label: '50kg' }
                  ]}
                  placeholder="Select weight"
                />

                <ProfessionalInput
                  label="Brand Name"
                  type="text"
                  value={formData.specifications.brand}
                  onChange={(e) => handleInputChange('specifications', { ...formData.specifications, brand: e.target.value })}
                  placeholder="Enter brand name"
                />
              </div>
            </div>
          )}

          {/* Product Images */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <ImageIcon className="w-5 h-5 mr-2 text-primary-600" />
              Product Images
            </h2>
            
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm font-medium text-gray-900">
                    Click to upload images
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG up to 5MB (Max 5 images)
                  </p>
                </label>
              </div>

              {images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.preview}
                        alt={`Product ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/seller/products')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Adding Product...' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProduct;
